import type { IncidentActionResult } from "@/types";
import type { ClaimKind } from "@/lib/trust";

export interface ResolutionEvidenceItem {
  label: string;
  value: string;
}

export interface ResolutionSummary {
  /** High-level section title */
  headline: string;
  /** Recommendation only — not a verified outcome */
  suggestedResolution: string;
  /** Only fields present in the action verification payload */
  evidence: ResolutionEvidenceItem[];
  verificationRequired: boolean;
  verified: boolean;
  /** Wall-clock elapsed for the action attempt (observation), not inferred MTTR */
  elapsedSeconds: number;
  claimKind: ClaimKind;
}

const SUGGESTED_BY_ACTION: Record<string, string> = {
  "tenant.restart": "Restart the tenant API container and re-run Runtime Doctor.",
  "restart-tenant-api": "Restart the tenant API container and re-run Runtime Doctor.",
  "tenant.redeploy": "Redeploy the tenant stack to align with the recorded manifest.",
  "redeploy-tenant": "Redeploy the tenant stack to align with the recorded manifest.",
  "backup.run": "Run a tenant backup and confirm an archive was recorded.",
  "run-backup": "Run a tenant backup and confirm an archive was recorded.",
  "email.reconnect": "Restart the API and re-check email integration.",
  "retry-email": "Restart the API and re-check email integration.",
  "verification.rerun": "Run Runtime Doctor and review the verification verdict.",
  "run-doctor": "Run Runtime Doctor and review the verification verdict.",
  "knowledgebase.reindex": "Rebuild the Knowledge Base index and verify readiness.",
};

function formatSummary(summary: unknown): string | null {
  if (summary == null) return null;
  if (typeof summary === "string" && summary.trim()) return summary.trim();
  if (typeof summary === "object" && !Array.isArray(summary)) {
    const s = summary as Record<string, unknown>;
    const pass = s.pass_count ?? s.pass;
    const warn = s.warn_count ?? s.warn;
    const fail = s.fail_count ?? s.fail;
    const parts: string[] = [];
    if (pass != null) parts.push(`pass ${String(pass)}`);
    if (warn != null) parts.push(`warn ${String(warn)}`);
    if (fail != null) parts.push(`fail ${String(fail)}`);
    if (parts.length) return parts.join(" · ");
  }
  return null;
}

/** Extract only real verification fields — never invent HTTP codes or restored-service claims. */
export function extractVerificationEvidence(
  result: IncidentActionResult,
): ResolutionEvidenceItem[] {
  const verification = result.verification ?? {};
  const evidence: ResolutionEvidenceItem[] = [];

  const verdict = verification.verdict;
  if (typeof verdict === "string" && verdict.trim()) {
    evidence.push({ label: "Runtime Doctor verdict", value: verdict.trim() });
  }

  const summaryText = formatSummary(verification.summary);
  if (summaryText) {
    evidence.push({ label: "Doctor summary", value: summaryText });
  }

  const backupAt = verification.backup_at;
  if (typeof backupAt === "string" && backupAt.trim()) {
    evidence.push({ label: "Backup recorded at", value: backupAt.trim() });
  }

  if (typeof result.message === "string" && result.message.trim()) {
    evidence.push({ label: "Action result", value: result.message.trim() });
  }

  if (typeof result.success === "boolean") {
    evidence.push({
      label: "Action reported success",
      value: result.success ? "true" : "false",
    });
  }

  return evidence;
}

export function buildResolutionSummary(
  actionId: string,
  result: IncidentActionResult,
  recoveryMs: number,
  resolved: boolean,
): ResolutionSummary {
  const evidence = extractVerificationEvidence(result);
  if (result.policySummary) {
    evidence.push({ label: "Policy", value: result.policySummary });
  }
  if (result.incidentResolved) {
    evidence.push({ label: "Incident", value: "resolved automatically" });
  }
  if (result.nextStep) {
    evidence.push({ label: "Recommended next step", value: result.nextStep });
  }
  const verdict =
    typeof result.verification?.verdict === "string" ? result.verification.verdict : null;
  const verified =
    resolved || result.verificationPassed === true || result.incidentResolved === true || verdict === "healthy";
  const suggestedResolution =
    SUGGESTED_BY_ACTION[actionId] ?? "Review verification evidence and decide next steps.";

  return {
    headline: verified ? "Verified outcome" : "Verification required",
    suggestedResolution,
    evidence,
    verificationRequired: !verified,
    verified,
    elapsedSeconds: Math.max(0, Math.round(recoveryMs / 1000)),
    claimKind: verified ? "verified" : "recommendation",
  };
}
